/**
 * From what the browser said to what goes on the wire — the ONE place the
 * built context becomes a request body, and the one place that decides which
 * purposes a visitor may usefully be asked about.
 *
 * ── WHY THIS IS A MODULE AND NOT FOUR LINES IN `useWidget.ts` ───────────────
 *
 * Because both decisions below are rules, and a rule inside a composable is a
 * rule no test in this app can reach: the unit runner is a hermetic node
 * environment (`packages/config-vitest/src/index.js`), so there is no jsdom and
 * no component render here at all. `visitor-context.ts` and `consent.ts` are
 * both built this way for the same reason and both say so in their headers.
 *
 * ── WHAT IT DELIBERATELY DOES NOT DO ───────────────────────────────────────
 *
 * It does not read `navigator`, does not touch storage, does not know what a
 * fetch is and has no clock. `readVisitorSignals` does the reading and
 * `api/widget.ts` does the sending; this is the pure middle, which is the part
 * where a mistake is silent.
 */
import type { WidgetVisitorContextRequest } from '../api/contract';

import { NOTICE_VERSION, type ConsentPurpose, type ConsentState } from './consent';
import { buildVisitorContext, type VisitorSignals } from './visitor-context';

/**
 * Which purposes there is any point asking about.
 *
 * A purpose whose DATA the host page has not supplied is not offered, and the
 * test is the raw signal rather than the built context: `buildVisitorContext`
 * omits `pagePath` both when the visitor refused AND when there was no path to
 * begin with, so reading the output could not tell "not permitted" from "not
 * available" — and offering a choice in the second case is asking a visitor to
 * decide about nothing.
 *
 * TODAY THIS RETURNS EMPTY IN PRODUCTION, and that is a named gap rather than a
 * bug: `loader/protocol.ts`'s `HostMessage` union carries `init`/`open`/`close`/
 * `theme` and no page fields, so `pagePath` and `referrer` are null at every
 * call site. Adding a message to that union is what makes the notice appear —
 * and doing it in this order is what stops a raw URL with a password-reset token
 * in its query string from crossing into this frame before anything existed to
 * refuse it.
 *
 * An EMPTY STRING counts as absent, not as a value. A host that sends `path: ''`
 * has told us nothing, and `pagePath()` in `visitor-context.ts` already maps it
 * to null one layer down; agreeing here is what stops the notice appearing for a
 * purpose whose value would be dropped anyway.
 */
export function availablePurposes(signals: VisitorSignals): readonly ConsentPurpose[] {
  const offered: ConsentPurpose[] = [];
  if (signals.pagePath !== null && signals.pagePath !== '') {
    offered.push('page-context');
  }
  if (signals.referrer !== null && signals.referrer !== '') {
    offered.push('arrival-context');
  }
  return offered;
}

/**
 * The request body for `widget.context.put`.
 *
 * ── THE TWO FIELDS THIS FUNCTION DROPS, AND WHY IT DROPS THEM HERE ──────────
 *
 * `buildVisitorContext` returns NINE members. Two of them never reach the
 * server:
 *
 *   `pageOrigin`  is already stored, in `widget_session.origin`, put there by
 *                 the bootstrap that CHECKED it against the brand's allowlist —
 *                 and that copy is browser-vouched (`event.origin`) where this
 *                 one would be a claim in a body. Two places to erase and two
 *                 answers to "which site was this", and the second answer would
 *                 be the forgeable one. `VISITOR_CONTEXT_DISCLOSURE` has said
 *                 `storedAs: 'widget_session.origin'` for this field since
 *                 before there was anywhere else to put anything.
 *   `viewport`    is refused by the contract, so sending it would be a 400. The
 *                 reason is in `WidgetVisitorContextSchema` and in migration
 *                 0051: a 50 px bucket persisted per PERSON is a fingerprint
 *                 component with a primary key, and it answers nothing a day
 *                 later — the module's own comment calls its value "a HINT, and
 *                 an agent debugging a layout has to ask".
 *
 * Dropping them by CONSTRUCTION — naming the five members that go, rather than
 * spreading the context and deleting two — is what makes a tenth field a
 * compile-time decision instead of something that ships by default.
 *
 * ── THE CONDITIONAL SPREAD IS THE REFUSAL PATH AND IT IS LOAD-BEARING ───────
 *
 * `pagePath` and `referrerOrigin` are spread only when `buildVisitorContext`
 * produced them, so a refused purpose leaves the KEY ABSENT — absent from
 * `Object.keys`, absent from `JSON.stringify`, and therefore absent from the
 * body the server parses. NOT `pagePath: context.pagePath` and not
 * `?? undefined`: with `exactOptionalPropertyTypes` those are different types,
 * and on the wire they are different VALUES — `JSON.stringify` drops an
 * `undefined` member, which would work by accident today and stop working the
 * moment anything between here and the server round-tripped the object.
 *
 * The server then converts absent to null and CLEARS the column, which is what
 * makes a withdrawal work: revoke, rebuild, resend, and the two values are gone
 * from the database in the same statement. `consent.ts`'s `revoke` notes that "a
 * caller that has cached a built context must rebuild it" — this function is the
 * rebuild, and it is called from an effect keyed on the consent state so the
 * rebuild is not something a caller has to remember.
 */
export function visitorContextBody(
  signals: VisitorSignals,
  consent: ConsentState,
): WidgetVisitorContextRequest {
  const context = buildVisitorContext(signals, consent);
  return {
    browser: context.browser,
    os: context.os,
    deviceClass: context.deviceClass,
    language: context.language,
    timeZone: context.timeZone,
    ...(context.pagePath === undefined ? {} : { pagePath: context.pagePath }),
    ...(context.referrerOrigin === undefined ? {} : { referrerOrigin: context.referrerOrigin }),
    // ALWAYS SENT, even when both optional fields are absent. It is what records
    // that this visitor was ASKED: a stored row with no path and a notice version
    // is "we showed them notice 1 and they said no", which is a different state
    // from "we never asked" and is the state a withdrawal produces. Migration
    // 0051's `person_visitor_context_consent_chk` refuses a stored path with no
    // version, so this is also the half of that constraint the client owes.
    noticeVersion: NOTICE_VERSION,
  };
}
